/**
 * ─── WHY THIS FILE'S COLOURS MOVED, AND WHAT IT SAYS ABOUT THE RATCHET ────
 *
 * `CreateTenantPage.jsx` has been on the closed-surface list since the
 * previous pass: it draws no colour the contrast suite cannot measure. That is
 * TRUE OF THE FILE and it was being read as true of the SCREEN. It is not.
 * This component renders inside that page's "Products & Pricing" card, and it
 * was carrying 44 lines of Tailwind's own palette.
 *
 * Measured in a browser over the compiled CSS, with the real Create Tenant
 * form rendered and the provisioning-unavailable state showing, in dark:
 *
 *     "Users"                      text-slate-700   1.76:1
 *     "Price book" / "Products" /
 *     "Discount" / "Currency"      text-slate-500   3.83:1
 *     "No active price book…"      text-amber-700   3.63:1
 *
 * Ten failing strings on the screen the whole programme is named after, none
 * of them visible to a per-file ratchet, because none of them is in the file
 * the ratchet had closed.
 *
 * The three native `<select>` elements are the ones that mattered most and
 * measured least: they carried `border-slate-200` with no ground and no ink at
 * all, so in dark the control's own edge vanished and its value inherited. The
 * treatment here is copied from the selects `CreateTenantPage` already draws
 * ten lines away — `border-input bg-card text-foreground`.
 *
 * `border-input`, not `border-border`, wherever the edge IS the control — a
 * select, a clickable product line, a toggle pill. WCAG 2.2 SC 1.4.11 puts a
 * 3:1 floor under that edge and `--border` is the decorative hairline with no
 * such floor. Panels that only group things visually keep `--border`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Package, Layers, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  PERCENT, DISCOUNT_KINDS, activeDiscount, discountPatch, discountAppliesTo,
} from './discountShape';
import {
  humanise, productLabel, entitlementText, problemText,
} from './productLabels';
import EditionPicker from './EditionPicker';
import { IncludedFeatureTicks, PriceRemedy } from './ProductAddons';
import DefaultPriceTag from './DefaultPriceTag';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Choose what the customer is buying.
 *
 * THIS COMPONENT DOES NO ARITHMETIC. Every price, subtotal, discount and total
 * on screen comes from POST /tenants/commercial/validate — the same function
 * tenant creation itself validates with. That is deliberate: a total computed
 * here could disagree with the one the server commits, and the operator would
 * have no way to know which was real. If you are tempted to multiply a
 * quantity by a price in this file, add it to the validator instead.
 *
 * "Unpriced" is likewise never rendered as 0. The server returns
 * `total: null` with a problem naming the line, and that is what is shown.
 */

/*
 * THE SECOND CATALOGUE THAT USED TO LIVE HERE IS GONE.
 *
 * `CAPABILITY_PRODUCTS` and `CAPABILITY_LABELS` were hand-kept mirrors of
 * `PRODUCT_CAPABILITIES` in `tenant_commercial_service.py`, and a hand-kept
 * mirror is a second authority however carefully it is written: it decided
 * which products existed, which were capabilities, and what they were called.
 * Adding a product to the catalogue did not add it here, which is exactly why
 * Data Manager never appeared in Tenant Creation at all.
 *
 * The selector now reads `GET /api/admin/commercial/presentation`, which is
 * derived at read time from `ENTITLEMENTS` + `CAPACITY_CATALOG` +
 * `PRODUCT_CAPABILITIES` and states its own contract: "presentation only;
 * item_code is the canonical identity and grouping never changes what is
 * provisioned".
 *
 * `product-prices` is no longer fetched here. Every field this component used
 * from it — `capacity_unit`, `capacity_block`, `capacity_included`, `add_on` —
 * is on the presentation row as `quantity_kind`, `block_size`,
 * `included_quantity` and `configuration_kind`. Money still comes from
 * `validate()`, which is the Price Book, so nothing in this file has ever
 * multiplied a quantity by a price and nothing here starts.
 */

/** Display text for a channel code the catalogue carries as an identifier.
 *
 * A DERIVATION, not a catalogue: it title-cases whatever the server sent and
 * upper-cases the known acronyms (`humanise` in productLabels). An unknown
 * channel renders as itself rather than disappearing, so a channel added
 * server-side shows up here the day it is added instead of the day someone
 * remembers to edit this file.
 */
const channelLabel = humanise;

/** Is this presentation row a product sold ONCE FOR THE WHOLE WORKSPACE?
 *
 * The server says so (`tenant_level`, derived from the catalogue's
 * `seat_based: False` on a product of its own). For a payload that predates
 * the field, the same fact from the row's shape: a sellable PRODUCT row (a
 * `tenant` or `channels` kind — never an add-on `capability`) that collects
 * no quantity. No product code is named, so the next tenant-level product is
 * covered the day it is catalogued.
 */
export const tenantLevelRow = (r) => {
  if (!r) return false;
  if (typeof r.tenant_level === 'boolean') return r.tenant_level;
  return !!r.selectable && r.quantity_kind === 'none'
    && (r.configuration_kind === 'tenant' || r.configuration_kind === 'channels');
};

/** Readable text from an API error.
 *
 * FastAPI returns a 422 `detail` as an ARRAY of error objects, and
 * `String(detail)` on that renders "[object Object]" — which is what the
 * operator saw whenever a value was refused by the request model rather than by
 * validate(). A string detail (our own refusals) still passes through unchanged.
 */
function _detailText(e) {
  const detail = e?.response?.data?.detail;
  if (!detail) return 'Could not validate this configuration against the server.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : null;
        return field ? `${field}: ${d.msg}` : d?.msg;
      })
      .filter(Boolean)
      .join('; ') || 'The configuration was refused.';
  }
  return 'Could not validate this configuration against the server.';
}

export default function CommercialConfigurator({ value, onChange, onValidityChange }) {
  const [mode, setMode] = useState(value?.bundle_code ? 'bundle' : 'manual');
  const [sections, setSections] = useState([]);
  // Code -> the name an operator reads, served beside the sections by
  // `product_labels()`. Every code this screen would otherwise print goes
  // through it, so `chatbot` / `ai_features` / `live_chat` never reach the page.
  const [labels, setLabels] = useState({});
  // Granted entitlements that are licensing plumbing, not products (Engage's
  // legacy Form Builder / Survey Builder codes). Served by the presentation
  // so the summary never names them; see `internal_grants` there.
  const [internalGrants, setInternalGrants] = useState([]);
  const [catalogueError, setCatalogueError] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` }), []);

  /* "Check again" beside an unpriced row: the operator has just set its price
     in Base Prices (another tab), so re-read the catalogue's prices without
     blanking the form, then re-validate the basket against the same book. */
  const [rechecking, setRechecking] = useState(false);
  const [recheckError, setRecheckError] = useState(null);
  const [catalogueTick, setCatalogueTick] = useState(0);
  const recheckPrices = useCallback(() => {
    setRechecking(true);
    setCatalogueTick((t) => t + 1);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [p, b, pb] = await Promise.all([
          // THE canonical catalogue projection. Sections, nesting, channels,
          // editions, capacity blocks and included-vs-selectable all come from
          // here. Deliberately NOT caught into an empty default: an empty
          // catalogue and an unreachable one are different facts, and the
          // second must not render as "this tenant can buy nothing".
          axios.get(`${API}/api/admin/commercial/presentation`, { headers }),
          axios.get(`${API}/api/admin/commercial/bundles`, { headers }).catch(() => ({ data: {} })),
          axios.get(`${API}/api/admin/price-books`, { headers }).catch(() => ({ data: {} })),
        ]);
        setSections(p.data.sections || []);
        setLabels(p.data.labels || {});
        setInternalGrants(p.data.internal_grants || []);
        setCatalogueError(null);
        setRecheckError(null);
        setBundles(b.data.bundles || b.data || []);
        setBooks((pb.data.price_books || []).filter((x) => x.status === 'active'));
      } catch (e) {
        // An unreachable catalogue must not look like an empty one. Rendering
        // zero products for a fetch failure tells the operator this tenant can
        // buy nothing, which is a different — and wrong — statement.
        if (catalogueTick > 0) {
          // A failed RE-read keeps the catalogue already on screen: the
          // operator asked whether a price had landed, not to lose the form.
          setRecheckError(_detailText(e) || 'The prices could not be re-read.');
        } else {
          setCatalogueError(_detailText(e) || 'The product catalogue could not be loaded.');
          setSections([]);
        }
      } finally {
        setLoading(false);
        setRechecking(false);
      }
    })();
  }, [headers, catalogueTick]);

  // Re-validate whenever the configuration changes. Debounced so typing a
  // quantity does not fire a request per keystroke.
  const revalidate = useCallback(async (cfg) => {
    // NO SHORT-CIRCUIT. This used to declare a configuration with no bundle
    // and no line invalid without asking, which was the client-side half of
    // the same defect the server had: CRM Console and Data Manager are both
    // included, so a customer who buys no add-on still receives a working
    // tenant. Deciding that here also meant the operator never saw WHY, and
    // had to tick a product nobody asked for to submit the form.
    //
    // The server owns validity. It still refuses an unstated user count, which
    // is what actually makes a configuration incomplete.
    setValidating(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/tenants/commercial/validate`, cfg, { headers });
      setValidation(data);
      // The PROBLEMS travel with the verdict. Reporting only `ok` left the
      // page with a disabled button and no way to say why — the server had
      // already named the cause ("conversation_center is enabled but no
      // capability is switched on") and the one screen that needed it threw
      // it away.
      onValidityChange?.(!!data.ok, data.problems || [], data.entitlements || []);
    } catch (e) {
      // A failed validation is NOT a valid configuration. Reporting nothing
      // here would leave the last good result on screen beside a broken one.
      setValidation({
        ok: false,
        problems: [{ field: 'commercial', code: 'validation_unavailable',
          detail: _detailText(e) }],
        totals: null, entitlements: [],
      });
      onValidityChange?.(false, [{
        field: 'commercial', code: 'validation_unavailable',
        detail: _detailText(e),
      }], []);
    } finally {
      setValidating(false);
    }
  }, [headers, onValidityChange]);

  // `catalogueTick` too: after "Check again" the basket is re-priced against
  // the book as it now stands, so a refusal that named the missing price
  // clears without the operator having to touch the form.
  useEffect(() => {
    const id = setTimeout(() => revalidate(value), 350);
    return () => clearTimeout(id);
  }, [value, revalidate, catalogueTick]);

  const patch = (next) => onChange({ ...value, ...next });

  /* Which presentation rows are priced per USER, from the projection rather
     than a list here. `seats` and `channels` both collect a user count
     (`quantity_kind === 'users'`); `capacity` collects blocks of something
     else and must never be seeded from the seat count. */
  const isSeatRow = (code) => {
    for (const sec of sections) {
      for (const r of sec.entitlements) {
        if (r.item_code === code) return r.quantity_kind === 'users';
      }
    }
    return false;
  };

  /* A SEAT-BASED PRODUCT DEFAULTS TO EVERY USER THE TENANT HAS.
     If a customer buys an app, they buy it for the people who work there —
     defaulting to 1 made the operator retype the seat count on every product
     and made "10 users, Booking for 1" the easy mistake to save. It is a
     DEFAULT, not a rule: the box stays editable downwards. */
  const seatDefault = () => (value.crm_seats && value.crm_seats > 0 ? value.crm_seats : 1);

  /* Which rows are TENANT-LEVEL products — the server's `tenant_level`, read
     from the catalogue (`seat_based: False` on a product of its own: AI
     Calling, Cluvik AI, and whatever is declared that way next). See
     `tenantLevelRow`. */
  const isTenantLevelRow = (code) => {
    for (const sec of sections) {
      for (const r of sec.entitlements || []) {
        if (r.item_code === code) return tenantLevelRow(r);
      }
    }
    return false;
  };

  /* A TENANT-LEVEL LINE CARRIES NO `quantity` AT ALL (owner, 6 Oct 2026).
     It used to carry `quantity: 1`, and on AI Calling that 1 travelled as "1
     user" into the entitlement, the licence and the admin's seats. The server
     reads an omitted quantity on such a line as one workspace, and refuses an
     omitted one on a seat-based line — so leaving the key out is a statement,
     not a gap. An existing line for such a product loses any stale quantity
     it carries, so the submitted payload never has one. */
  const setLine = (code, changes) => {
    const lines = [...(value.lines || [])];
    const i = lines.findIndex((l) => l.item_code === code);
    const tenantLevel = isTenantLevelRow(code);
    if (i === -1) {
      lines.push({
        item_code: code,
        // A seat row defaults to every user; an add-on keeps its 1 (its
        // quantity is discarded server-side); a tenant-level product has none.
        ...(tenantLevel ? {} : { quantity: isSeatRow(code) ? seatDefault() : 1 }),
        capabilities: [],
        ...changes,
      });
    } else lines[i] = { ...lines[i], ...changes };
    if (tenantLevel) {
      const at = i === -1 ? lines.length - 1 : i;
      const { quantity: _dropped, ...withoutQuantity } = lines[at];
      lines[at] = withoutQuantity;
    }
    patch({ lines });
  };

  /* Changing the tenant's user count carries the seat rows with it.
     Rows still sitting on the previous default were tracking it and follow;
     a row the operator deliberately lowered keeps its number, but is clamped
     so no product can be assigned to more users than the tenant has — which is
     what the Users caption already promises and the validator already refuses.
     Capacity rows are untouched: their quantity is blocks, not people. */
  const setSeats = (next) => {
    const previous = value.crm_seats;
    const lines = (value.lines || []).map((l) => {
      if (!isSeatRow(l.item_code)) return l;
      if (!next || next < 1) return l;
      if (previous && l.quantity === previous) return { ...l, quantity: next };
      return l.quantity > next ? { ...l, quantity: next } : l;
    });
    onChange({ ...value, crm_seats: next, lines });
  };

  /* Unticking a product also forgets its edition choice. Leaving
     `product_editions.prospect_finder = automation` behind would make the
     server refuse "Automation needs Prospect Finder" for a row the operator
     has just switched off. */
  /* ...and its ADD-ONS. Surveys and Landing Pages exist only beneath Engage
     (`add_on_of` on their rows); leaving them ticked after Engage is
     unticked would make the server refuse "Surveys is an add-on to Engage"
     for rows the operator can no longer even toggle. */
  const addOnsOf = (code) => sections.flatMap((sec) => (sec.entitlements || [])
    .filter((r) => r.add_on_of === code).map((r) => r.item_code));

  const removeLine = (code) => {
    const editions = { ...(value.product_editions || {}) };
    delete editions[code];
    const dropped = new Set([code, ...addOnsOf(code)]);
    patch({
      lines: (value.lines || []).filter((l) => !dropped.has(l.item_code)),
      ...(value.product_editions ? { product_editions: editions } : {}),
    });
  };

  const lineFor = (code) => (value.lines || []).find((l) => l.item_code === code);
  const rowLabel = (code) => {
    for (const sec of sections) {
      for (const r of sec.entitlements || []) {
        if (r.item_code === code) return r.label || productLabel(code, labels);
      }
    }
    return productLabel(code, labels);
  };

  /* Products priced ONCE for the workspace — the tenant-level rows. Their
     entitlement carries no user seats, so the summary says "whole workspace"
     rather than "× N users". Not `configuration_kind === 'tenant'` alone: AI
     Calling is a `channels` row that is just as per-tenant, and the summary
     printed it as "AI Calling × 1 user". */
  const perTenant = new Set(sections.flatMap((sec) => (sec.entitlements || [])
    .filter(tenantLevelRow).map((r) => r.item_code)));

  const switchMode = (m) => {
    setMode(m);
    patch(m === 'bundle' ? { lines: [] } : { bundle_code: null });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading catalogue…
      </div>
    );
  }

  const problems = validation?.problems || [];
  const totals = validation?.totals;

  return (
    <div className="space-y-5" data-testid="commercial-configurator">
      {/* mode */}
      <div className="flex gap-2">
        <Button type="button" variant={mode === 'manual' ? 'default' : 'outline'} size="sm"
                onClick={() => switchMode('manual')} data-testid="mode-manual">
          <Package className="h-4 w-4 mr-2" /> Select products
        </Button>
        <Button type="button" variant={mode === 'bundle' ? 'default' : 'outline'} size="sm"
                onClick={() => switchMode('bundle')} data-testid="mode-bundle">
          <Layers className="h-4 w-4 mr-2" /> Use a bundle
        </Button>
      </div>

      {/* price book */}
      <div>
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Price book</label>
        <select
          className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground"
          value={value.price_book_code || ''}
          onChange={(e) => patch({ price_book_code: e.target.value || null })}
          data-testid="price-book-select"
        >
          <option value="">Default price book</option>
          {books.map((b) => (
            <option key={b.book_code} value={b.book_code}>
              {b.name} ({b.currency})
            </option>
          ))}
        </select>
        {books.length === 0 && (
          <p className="text-xs text-warning-text mt-1">
            No active price book exists yet — every line will resolve as Unpriced until one is created.
          </p>
        )}
      </div>

      {mode === 'bundle' ? (
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Bundle</label>
          <select
            className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground"
            value={value.bundle_code || ''}
            onChange={(e) => patch({ bundle_code: e.target.value || null })}
            data-testid="bundle-select"
          >
            <option value="">Choose a bundle…</option>
            {bundles.map((b) => (
              <option key={b.bundle_code} value={b.bundle_code}>{b.name}</option>
            ))}
          </select>
          {bundles.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">No bundles defined yet.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/*
            USERS — the tenant-level quantity, stated once.

            CRM Console is an included base application and is deliberately NOT
            made sellable to carry this number. Before it existed the tenant's
            enforced user limit was max(seats) across whatever happened to be
            sold, which moved when an unrelated module changed and meant nothing
            for a tenant who bought no seat-based product at all.
          */}
          <div className="rounded-lg border border-border p-3 flex items-center justify-between gap-3"
               data-testid="crm-seats-row">
            <div>
              <p className="text-sm font-medium text-foreground">Users</p>
              <p className="text-xs text-muted-foreground">
                The tenant's user limit, and the most any seat-based product can
                be assigned to.
              </p>
            </div>
            <Input type="number" min={1} className="w-24 h-8"
                   value={value.crm_seats ?? ''}
                   onChange={(e) => setSeats(
                     // Number('') is 0, and 0 is refused by the request model
                     // BEFORE validate() runs — so clearing the field produced a
                     // raw 422 instead of the written "state how many CRM users"
                     // message. Empty means unstated, which is what null says.
                     e.target.value === '' ? null : Number(e.target.value))}
                   data-testid="qty-crm-seats" />
          </div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Products</label>
          {catalogueError && (
            <p className="text-sm text-destructive-text bg-destructive/10 border border-destructive/25 rounded px-3 py-2"
               data-testid="catalogue-error">
              {catalogueError}
            </p>
          )}
          {!catalogueError && sections.length === 0 && (
            <p className="text-xs text-muted-foreground">No products in the catalogue.</p>
          )}

          {/*
            RENDERED FROM THE CANONICAL PROJECTION, SECTION BY SECTION.

            Order, titles, nesting, which rows are selectable and which are
            included, the quantity unit, the block size and the channel list all
            arrive on the payload. Nothing here decides that a code is a
            product, a capability or a capacity — that decision has one owner
            and this file is not it.
          */}
          {sections.map((sec) => {
            const rows = sec.entitlements || [];
            const parents = rows.filter((r) => !r.nested_under);
            const childrenOf = (code) => rows.filter((r) => r.nested_under === code);
            // The de-duplication that used to live here is gone. A capability
            // with its own row is no longer emitted as a channel of its parent
            // — fixed at the canonical owner, because suppressing a duplicate
            // in the consumer leaves the duplicate in the contract.

            const renderRow = (r, depth) => {
              const code = r.item_code;
              const line = lineFor(code);
              const kind = r.configuration_kind;
              const selectable = r.selectable;
              const channels = r.channels || [];
              // DOES THIS ROW COLLECT A NUMBER AT ALL? The catalogue's answer,
              // `quantity_kind`, and nothing else. The kind says what SHAPE the
              // choice has; it does not say whether a quantity comes with it.
              // Deciding the box on the kind alone drew a "Users" box on AI
              // Calling — a `channels` row priced per tenant, served
              // `quantity_kind: 'none'` — and its "1 user" reached the
              // entitlement, the licence and the admin's seats (tenant
              // c81d4516, 5 Oct 2026). Any future tenant-level row with
              // sub-choices is covered the same way, with no code named here.
              // Only an explicit 'none' withholds the box: a row that states
              // no `quantity_kind` keeps the behaviour it always had. A
              // tenant-level product never collects one, whatever else it says.
              const tenantLevel = tenantLevelRow(r);
              const collectsQuantity = r.quantity_kind !== 'none' && !tenantLevel;
              const showsQuantity = selectable && collectsQuantity && (kind === 'seats' || kind === 'capacity' || kind === 'channels');
              // AN ADD-ON OF A PRODUCT THAT MUST BE IN THE BASKET (Surveys,
              // Landing Pages -> Engage). The server states the owner on the
              // row; the toggle stays disabled until that product is ticked,
              // which is the same rule `validate()` enforces
              // (`addon_without_product`) — this only agrees with it first.
              const ownerCode = r.add_on_of;
              const ownerMissing = !!ownerCode && !lineFor(ownerCode);
              const ownerName = ownerCode ? (r.add_on_of_label || rowLabel(ownerCode)) : null;

              return (
                <div key={code}
                     className={`rounded-lg border p-3 ${depth ? 'ml-6' : ''} ${
                       line ? 'border-primary/40 bg-primary/5' : 'border-input'}`}
                     data-testid={`row-${code}`}>
                  <div className="flex items-center justify-between gap-3">
                    <label className={`flex items-center gap-2 text-sm font-medium ${
                      selectable && !(ownerMissing && !line) ? 'cursor-pointer' : 'text-muted-foreground'}`}>
                      {selectable ? (
                        <input type="checkbox" checked={!!line}
                               // Unticking is always allowed; ticking needs
                               // the product it is an add-on to.
                               disabled={ownerMissing && !line}
                               onChange={(e) => (e.target.checked ? setLine(code, {}) : removeLine(code))}
                               data-testid={`product-${code}`} />
                      ) : (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded
                                         bg-muted text-muted-foreground border border-border"
                              data-testid={`included-badge-${code}`}>
                          Included
                        </span>
                      )}
                      {r.label || productLabel(code, labels)}
                      {selectable && (
                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                          line ? 'bg-primary text-primary-foreground border-primary'
                               : 'bg-card text-muted-foreground border-input'}`}
                              data-testid={`state-${code}`}>
                          {line ? 'Selected' : 'Not selected'}
                        </span>
                      )}
                    </label>

                    {/* THE DEFAULT PRICE, AS THE BOOK STATES IT. Resolved on
                        the server through the default price book and worded
                        there; nothing here multiplies or formats money. Shown
                        before the row is ticked, unit included, so an operator
                        knows Assistants is per user and Cluvik AI per tenant
                        before choosing. An unpriced row says which book it is
                        missing from, never a zero. */}
                    {/* The same component Edit Tenant's product card draws,
                        so the two screens cannot price a product
                        differently. */}
                    {selectable && r.default_price && (
                      <DefaultPriceTag code={code} price={r.default_price}
                                       onRecheck={recheckPrices} rechecking={rechecking}
                                       recheckError={recheckError} />
                    )}

                    {/* Two kinds, two words. `capability` is an add-on that
                        attaches to a product; `tenant` is a product of its own
                        sold once for the workspace. Both take no quantity, and
                        labelling them the same would tell the operator that a
                        row which earns an app-switcher tile is a capability of
                        something else.
                        A `channels` row that collects no quantity (AI Calling)
                        is a product sold once for the workspace too, and reads
                        exactly as a `tenant` row does. */}
                    {selectable
                      && (kind === 'capability' || kind === 'tenant'
                          || (kind === 'channels' && !collectsQuantity)) && (
                      <span className="text-xs text-muted-foreground" data-testid={`unit-${code}`}>
                        {kind === 'capability'
                          ? (ownerCode ? 'Optional add-on' : 'Tenant capability')
                          : 'Whole workspace'}
                      </span>
                    )}

                    {showsQuantity && line && (
                      <div className="flex items-center gap-2">
                        {/* The unit and the block size are the catalogue's, and
                            a block size above 1 is stated: a field labelled
                            plainly "records" that collects blocks of 10,000 is
                            wrong by four orders of magnitude. */}
                        <span className="text-xs text-muted-foreground" data-testid={`unit-${code}`}>
                          {kind === 'capacity'
                            ? (r.block_size > 1
                                ? `x ${r.block_size.toLocaleString()} ${r.quantity_kind}`
                                : r.quantity_kind)
                            : 'Users'}
                        </span>
                        <Input type="number" min={1}
                               /* Capacity is blocks and has no seat ceiling. */
                               max={kind === 'capacity' ? undefined : (value.crm_seats || undefined)}
                               className="w-24 h-8"
                               value={line.quantity}
                               onChange={(e) => setLine(code, { quantity: Number(e.target.value) })}
                               data-testid={`qty-${code}`} />
                      </div>
                    )}
                  </div>

                  {selectable && r.description && (
                    <p className="mt-1 pl-6 text-xs text-muted-foreground"
                       data-testid={`description-${code}`}>
                      {r.description}
                    </p>
                  )}

                  {ownerMissing && !line && (
                    <p className="mt-1 pl-6 text-xs text-muted-foreground"
                       data-testid={`addon-needs-${code}`}>
                      Select {ownerName} to add {r.label || productLabel(code, labels)}.
                    </p>
                  )}

                  {/* WHAT EVERY BUYER OF THIS PRODUCT HAS (Engage: Forms,
                      Prospect Capture). Read-only ticks — part of the
                      product, never a choice and never a price. */}
                  {selectable && (
                    <IncludedFeatureTicks code={code} features={r.included_features}
                                          productLabel={r.label || productLabel(code, labels)} />
                  )}

                  {/* WHAT THE SALE BRINGS WITH IT. Live Chat has no row and no
                      price of its own — it is part of Assistants — so the
                      Assistants row has to say so, or the operator cannot
                      tell that selling it gives the customer Live Chat. */}
                  {selectable && (r.includes || []).length > 0 && (
                    <p className="mt-1 pl-6 text-xs text-foreground" data-testid={`includes-${code}`}>
                      Includes {r.includes.map((i) => i.label).join(', ')} — no separate
                      line or price
                    </p>
                  )}

                  {r.included_quantity != null && (
                    <p className="mt-1 pl-6 text-xs text-muted-foreground" data-testid={`included-${code}`}>
                      {/* `unit` (records, GB), not `quantity_kind` — which is
                          "none" now that capacity is not a purchasable line
                          here, and printed "50,000 none" on the live page. */}
                      {r.included_quantity.toLocaleString()} {r.unit} included with
                      every tenant. Change this tenant's allowance in Quotas
                      after it is created.
                    </p>
                  )}

                  {/* THE EDITION IS A REAL CHOICE AND IT TRAVELS.
                      `product_editions` carries {product: edition} through the
                      config, the frozen spec (where it is hashed) and on to
                      entitlement. Generic rather than a Data-Manager boolean,
                      so the next product with editions needs no schema change.
                      The code is the identity; the label is what is read. */}
                  {/* Offered on an INCLUDED row (Data Manager) always, and on
                      a SELLABLE row (Prospect Finder) once it is selected —
                      Automation of a product nobody bought is refused by the
                      server, so it is not offered. The picker renders what the
                      row carries: labels, prices, what Automation includes,
                      and the AI note. See EditionPicker. */}
                  {r.editions && (!selectable || line) && (
                    <EditionPicker
                      code={code}
                      row={r}
                      value={(value.product_editions || {})[code]}
                      onChange={(ed) => patch({
                        product_editions: { ...(value.product_editions || {}), [code]: ed },
                      })}
                    />
                  )}

                  {line && channels.length > 0 && (
                    <div className="mt-3 pl-6">
                      <p className="text-xs text-muted-foreground mb-1">
                        Capabilities — part of this one product, not separate products
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {channels.map((c) => {
                          const on = (line.capabilities || []).includes(c);
                          return (
                            <button key={c} type="button"
                                    data-testid={`cap-${code}-${c}`}
                                    onClick={() => setLine(code, {
                                      capabilities: on
                                        ? line.capabilities.filter((x) => x !== c)
                                        : [...(line.capabilities || []), c],
                                    })}
                                    className={`px-2.5 py-1 rounded-full text-xs border transition ${
                                      on ? 'bg-primary text-primary-foreground border-primary'
                                         : 'bg-card text-muted-foreground border-input'}`}>
                              {channelLabel(c)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            };

            /* A SECTION WITH NOTHING TO DECIDE IS A LIST, NOT A FORM.
               Every row here is included and none is selectable, so a stack of
               full-height bordered cards with an "Included" badge on each spent
               a screen and a half of scrolling to say one sentence. It also
               grows: the list is derived from the catalogue, so shipping
               features makes the page longer. Rendered as chips it stays two or
               three lines however many there are. Sections with real controls
               are untouched. */
            const decorative = sec.entitlements.every((r) => !r.selectable);

            return (
              <details key={sec.section} open={!sec.collapsed_by_default}
                       className="rounded-lg border border-border"
                       data-testid={`section-${sec.section}`}>
                <summary className="px-3 py-2 text-sm font-semibold text-foreground cursor-pointer">
                  {sec.title}
                  {decorative && (
                    <span className="ml-2 font-normal text-xs text-muted-foreground">
                      {sec.entitlements.length} included
                    </span>
                  )}
                </summary>
                {decorative ? (
                  <div className="px-3 pb-3 flex flex-wrap gap-1.5"
                       data-testid={`section-tags-${sec.section}`}>
                    {sec.entitlements.map((r) => (
                      <span key={r.item_code}
                            title={r.description || undefined}
                            className="px-2 py-0.5 rounded-full text-xs bg-muted
                                       text-muted-foreground border border-border"
                            data-testid={`tag-${r.item_code}`}>
                        {r.label || productLabel(r.item_code, labels)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 pt-0 space-y-2">
                    {parents.map((r) => (
                      <div key={r.item_code} className="space-y-2">
                        {renderRow(r, 0)}
                        {childrenOf(r.item_code).map((c) => renderRow(c, 1))}
                      </div>
                    ))}
                  </div>
                )}
              </details>
            );
          })}
        </div>
      )}

      {/* discount — TWO SHAPES, ONE AT A TIME. The commercial model carries a
          percentage and a flat amount, and the server refuses a configuration
          with both set. Only the percentage was reachable here, so the flat
          shape existed everywhere except where an operator could use it. */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Discount</label>
          <div className="mt-1 flex gap-2">
            <select
              className="h-9 rounded-md border border-input bg-card px-2 text-sm text-foreground"
              value={activeDiscount(value).kind}
              onChange={(e) => patch(discountPatch(e.target.value, activeDiscount(value).value))}
              data-testid="discount-kind"
            >
              {DISCOUNT_KINDS.map((k) => (
                <option key={k.kind} value={k.kind}>{k.label}</option>
              ))}
            </select>
            <Input type="number" min={0}
                   max={activeDiscount(value).kind === PERCENT ? 100 : undefined}
                   className="flex-1"
                   value={activeDiscount(value).value || 0}
                   onChange={(e) => patch(discountPatch(activeDiscount(value).kind, e.target.value))}
                   data-testid="discount-percent" />
          </div>
          {/* A discount against a basket that costs nothing produces no visible
              change, and the field looks broken rather than inapplicable. */}
          {activeDiscount(value).value > 0 && discountAppliesTo(totals) && (
            <p className="mt-1 text-xs text-warning-text" data-testid="discount-applies-to">
              {discountAppliesTo(totals)}
            </p>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Currency</label>
          <Input className="mt-1" value={value.currency || 'USD'}
                 onChange={(e) => patch({ currency: e.target.value.toUpperCase().slice(0, 3) })}
                 data-testid="currency" />
        </div>
      </div>

      {/* what the SERVER says. Nothing below is computed here. */}
      <div className="rounded-lg border border-border p-4 bg-muted/50" data-testid="commercial-summary">
        {validating ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking configuration…
          </div>
        ) : !validation ? (
          <p className="text-sm text-muted-foreground">Select a product or a bundle to see pricing.</p>
        ) : (
          <>
            {problems.length > 0 && (
              <div className="mb-3 space-y-1" data-testid="commercial-problems">
                {problems.map((p, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-destructive-text">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      {problemText(p)}
                      {/* An unpriced line priced against the default book
                          carries the fix (`remedy`); one priced against a
                          named book does not — Base Prices writes the
                          default. */}
                      {p.code === 'unpriced' && p.remedy && (
                        <span className="block mt-0.5">
                          <PriceRemedy remedy={p.remedy} onRecheck={recheckPrices}
                                       rechecking={rechecking}
                                       testId={`problem-remedy-${p.remedy.item_code}`} />
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {validation.included_baseline && (
            <p className="text-xs text-muted-foreground bg-muted/50 border border-border
                          rounded px-3 py-2" data-testid="included-baseline-note">
              <strong>Included baseline.</strong> No add-on product is being sold.
              The tenant still receives every included capability and the licences
              they require. The total below is the sum of no priced lines — not a
              plan, and not a discount.
            </p>
          )}
          {validation.entitlements?.length > 0 && (() => {
              // Purchased lines are the operator's decision and get the space.
              // The `included` baseline — CRM Console, Setup, Reporting and the
              // rest of the platform's own furniture — is every tenant's, and
              // listing eighteen "× 0" badges buried the two products actually
              // being bought. Counted, not enumerated, and still visible.
              // A grant that is licensing plumbing (Engage -> the legacy Form
              // Builder / Survey Builder codes) is not something the customer
              // bought or sees, so it is not listed; Live Chat, which IS a
              // product experience of Assistants, still is.
              const internal = new Set(internalGrants);
              const purchased = validation.entitlements.filter((e) => e.source !== 'included'
                && !(e.source === 'granted' && internal.has(e.app_code)));
              const included = validation.entitlements.filter((e) => e.source === 'included');
              return (
                <div className="mb-3">
                  <div className="flex flex-wrap gap-1.5">
                    {purchased.map((e) => (
                      <Badge key={e.app_code} variant="outline" className="text-xs"
                             data-testid={`entitlement-${e.app_code}`}>
                        {entitlementText(e, labels, perTenant)}
                      </Badge>
                    ))}
                  </div>
                  {included.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1.5" data-testid="included-entitlements"
                       title={included.map((e) => productLabel(e.app_code, labels)).join(', ')}>
                      + {included.length} included platform entitlements
                      (CRM Console, Setup, Reporting and others — part of every tenant)
                    </p>
                  )}
                </div>
              );
            })()}

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total</span>
              {totals?.priced ? (
                <span className="text-lg font-semibold" data-testid="commercial-total">
                  {totals.currency} {Number(totals.total).toFixed(2)}
                </span>
              ) : (
                // NEVER a zero. The server said it could not price this.
                <span className="text-sm font-medium text-warning-text" data-testid="commercial-unpriced">
                  Unpriced — not zero
                </span>
              )}
            </div>
            {totals?.priced && totals.discount_total > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Subtotal {totals.currency} {Number(totals.subtotal).toFixed(2)} · discount −
                {totals.currency} {Number(totals.discount_total).toFixed(2)}
              </p>
            )}
            {validation.ok && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-success">
                <CheckCircle2 className="h-3.5 w-3.5" /> Configuration is valid and can be provisioned
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
